import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Typography, Table, Col } from "antd";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { documentsColumn } from "utils/tableColums";
import { renderDpatEvidenceLink, isEvidenceLinkUploaded, documentHubLinksComplete } from "utils/renderDpatEvidenceLink";
import { formatDataGeneral, getFileLinkIfExist } from "utils/utils";

const { Title, Text, Link } = Typography;

const MEETING_COLUMNS = [
  { title: "Minute Ref.", dataIndex: "meeting", key: "meeting" },
  { title: "Meeting Type", dataIndex: "meetingType", key: "meetingType" },
  { title: "Minutes Link", dataIndex: "minutes", key: "minutes" },
];

const MINUTES_LINK_KEYS = ["minutes"];

function scrollToIndicator(slug) {
  document.querySelector(`[data-dpat-indicator="${slug}"]`)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function ExecutiveCommitteeGuide() {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm" style={{ marginTop: "16px" }}>
      <Text>
        Executive Committee meeting minutes — see{" "}
        <Link onClick={() => scrollToIndicator("c2.0-2.1")}>CI 2.1</Link>
      </Text>
    </div>
  );
}

function buildAuditorGeneralReportLinks(documents) {
  const documentData = documents?.data;
  const documentReports = documents?.reports;
  const auditorGeneralDocs = formatDataGeneral(
    documentData,
    "DPAT | M&E Tool Type",
    "Auditor-General Reports",
  ) || [];

  let auditorGeneralReportLink = "";
  if (auditorGeneralDocs.length > 0) {
    auditorGeneralReportLink = getFileLinkIfExist(
      documentReports,
      "MjPmcbueEdU",
      auditorGeneralDocs[0]?.trackedEntity,
    );
  }

  return [
    {
      title: "Auditor-General Reports",
      link: auditorGeneralReportLink
        ? `https://dddp.gov.gh/api/events/files?eventUid=${auditorGeneralReportLink}&dataElementUid=MjPmcbueEdU`
        : "Not Uploaded",
    },
  ];
}

const AuditorGeneralGAMeeting = forwardRef(({
  gaMeetings,
  documents,
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  isAssessmentStarted,
}, ref) => {
  const [fulfillment, setFulfillment] = useState("Not Fulfilled");
  const [data, setData] = useState([]);
  const [links, setLinks] = useState([]);

  const meetingTableColumns = useMemo(
    () => MEETING_COLUMNS.map((col) =>
      MINUTES_LINK_KEYS.includes(col.dataIndex)
        ? {
            ...col,
            render: (value) => renderDpatEvidenceLink(value, "View Minutes"),
          }
        : col
    ),
    [],
  );

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
            ...col,
            render: (value) => renderDpatEvidenceLink(value, "View Document"),
          }
        : col
    ),
    [],
  );

  useEffect(() => {
    if (isAssessmentStarted) {
      if (savedAssessmentData) {
        setData(savedAssessmentData.data ?? []);
        setLinks(savedAssessmentData.links ?? []);
        setFulfillment(savedAssessmentData.fulfillment ?? "Not Fulfilled");
      }
      return;
    }

    const temp = [];
    let nextFulfillment = "Fulfilled";
    const documentLinks = buildAuditorGeneralReportLinks(documents);

    if (gaMeetings?.meetings) {
      gaMeetings.meetings.forEach((val) => {
        temp.push({
          key: val.key,
          meeting: val.signatoriesMinutes,
          meetingType: "GA Meeting",
          minutes: val.docs,
        });

        if (!isEvidenceLinkUploaded(val.docs)) {
          nextFulfillment = "Not Fulfilled";
        }
      });
    }

    if (!documentHubLinksComplete(documentLinks)) {
      nextFulfillment = "Not Fulfilled";
    }

    if (temp.length === 0) {
      nextFulfillment = "Not Fulfilled";
    }

    setLinks(documentLinks);
    setFulfillment(nextFulfillment);
    setData(temp);
  }, [districtId, year, gaMeetings, documents, isAssessmentStarted, savedAssessmentData]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI4",
      area: "Transparency, Accountability & Participation",
      data,
      fulfillment,
      links,
    }),
  }));

  const DPAT_SLUG = "c4.0-4.3";

  return (
    <Comment
      data={gaMeetings}
      year={year}
      districtId={districtId}
      tableCommentedId={`c4.0-4.3-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
          />

          <Col align="start">
            <CiResultDisplay slug={DPAT_SLUG} fulfillment={fulfillment} />
          </Col>

          <ExecutiveCommitteeGuide />

          <Title level={4} style={{ marginTop: "20px" }}>
            Auditor General Report to the General Assembly
          </Title>

          {data.length > 0 && (
            <Table
              columns={meetingTableColumns}
              dataSource={data}
              pagination={false}
              bordered
              rowKey={(record, index) => record.key || index}
            />
          )}

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Attached Documents</Title>
          <Table
            columns={documentTableColumns}
            dataSource={links}
            pagination={false}
            bordered
            rowKey="title"
          />

          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default AuditorGeneralGAMeeting;
